import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import WebGLVectorLayer from 'ol/layer/WebGLVector';
import VectorSource from 'ol/source/Vector';
import { containsExtent, isEmpty as isExtentEmpty } from 'ol/extent';

import { useProject } from '@contexts/Project.context';
import { getMasterFeatures } from '@/utils/api';
import { createFeature, noOverlapOffset, deleteLayerByCustomId } from '@utils/map/helpers';
import LayerSettings from '../sidebars/sidebarElements/LayerSettings';

// Local Style Constants
const STROKE_WIDTH = 4;

// Arrow keys OL's KeyboardPan interaction reacts to (see ol/interaction/KeyboardPan).
const PAN_KEYS = new Set(['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight']);

// Elements where an arrow keypress edits text/selection rather than panning
// the map, mirroring OL's own `targetNotEditable` condition. KeyboardPan
// itself ignores arrow keys typed into these, so our "did the user move the
// map" tracking must too.
const EDITABLE_TAGS = new Set(['INPUT', 'SELECT', 'TEXTAREA']);

/**
 * Whether a keydown event corresponds to OL's KeyboardPan interaction
 * actually panning the map (i.e. it would set userMovedRef). Exported for
 * unit testing in isolation from the effect/DOM wiring.
 *
 * Mirrors KeyboardPan's default condition (noModifierKeys && targetNotEditable,
 * see ol/interaction/KeyboardPan + ol/events/condition): a modifier key held
 * down (Alt/Ctrl/Meta/Shift) makes OL ignore the arrow key entirely — e.g.
 * Ctrl+ArrowLeft / Cmd+ArrowLeft are common OS/browser text-navigation
 * shortcuts, not map panning — so without this guard those keypresses would
 * be wrongly treated as the user having moved the map.
 */
export const isKeyboardPanEvent = event =>
	PAN_KEYS.has(event.key) &&
	!event.altKey &&
	!event.ctrlKey &&
	!event.metaKey &&
	!event.shiftKey &&
	!EDITABLE_TAGS.has(event.target?.tagName) &&
	!event.target?.isContentEditable;

/** Local Style For AOI Layer */
const STYLE = {
	'stroke-color': 'rgba(255, 255, 0, 0.8)',
	'stroke-width': STROKE_WIDTH,
	'stroke-offset': noOverlapOffset(STROKE_WIDTH),
	'stroke-line-dash': [10, 10],
};

/* This component is responsible for rendering the area of interest layer and toggling its visibility. */
const AoiLayer = ({ layerId = 'aoiLayer' }) => {
	const { mapObject, project, pickedTask, defaultFormat } = useProject();

	const { uuid: projectUuid } = project ?? {};
	const { model_uuid: modelUuid } = pickedTask ?? {};

	const [layer, setLayer] = useState(null);

	// Tracks the model load we've already auto-fitted for, so we fit once per
	// load and don't fight the user's zoom on filter changes or 5-min refetches.
	const autoFitKeyRef = useRef(null);

	// Set once the user pans/zooms the map themselves. After that we never
	// auto-fit, so a late-resolving boundary can't override deliberate navigation.
	const userMovedRef = useRef(false);

	// Reset the flag when the project or model changes so the new context gets
	// a fresh chance to auto-fit before the user interacts with the map.
	useEffect(() => {
		userMovedRef.current = false;
	}, [projectUuid, modelUuid]);

	// Flag genuine user gestures (drag-pan, wheel-zoom, double-click zoom,
	// keyboard pan) — not the programmatic view changes our own fit()
	// triggers, which would false-positive if we listened to view
	// change:center/change:resolution instead.
	// Keyboard pan is routed to `document` (see keyboardEventTarget in
	// Map.jsx), not to the map's own target element, so that listener must
	// be attached to document rather than mapObject/viewport.
	useEffect(() => {
		if (!mapObject) return;

		const markMoved = () => {
			userMovedRef.current = true;
		};

		// Keyboard pan fires for arrow keys only, and only when focus isn't in
		// an editable field — matching KeyboardPan's own behavior so typing in
		// an unrelated form field (e.g. renaming a filter) doesn't get
		// mistaken for the user navigating the map.
		const markMovedOnPanKey = event => {
			if (isKeyboardPanEvent(event)) markMoved();
		};

		const viewport = mapObject.getViewport();
		mapObject.on('pointerdrag', markMoved);
		mapObject.on('dblclick', markMoved);
		viewport?.addEventListener('wheel', markMoved, { passive: true });
		document.addEventListener('keydown', markMovedOnPanKey);

		return () => {
			mapObject.un('pointerdrag', markMoved);
			mapObject.un('dblclick', markMoved);
			viewport?.removeEventListener('wheel', markMoved);
			document.removeEventListener('keydown', markMovedOnPanKey);
		};
	}, [mapObject]);

	const { data, error } = useQuery({
		queryKey: ['fetchAOI', projectUuid, modelUuid],
		queryFn: () => getMasterFeatures(projectUuid, modelUuid, 'detected_areas'),
		enabled: !!projectUuid && !!modelUuid,
		refetchInterval: 60_000 * 5, // 5min
		refetchIntervalInBackground: true,
	});

	const { features: rawFeatures = [] } = data ?? {};

	// Creates the AOI Layer
	useEffect(() => {
		const layerInstance = new WebGLVectorLayer({
			name: 'Model boundary',
			zIndex: 11,
			visible: true,
			source: new VectorSource({}),
			properties: { customLayerId: layerId },
			style: {},
		});

		mapObject.addLayer(layerInstance);
		setLayer(layerInstance);
		return () => {
			deleteLayerByCustomId(mapObject, layerId);
			setLayer(null);
		};
	}, []);

	useEffect(() => {
		if (error) return console.error('Error fetching aoi layer: ', error);
		if (!layer || !rawFeatures) return;

		const features = rawFeatures.reduce((acc, { geometry, properties }) => {
			const feature = createFeature(geometry, properties);
			if (feature) acc.push(feature);
			return acc;
		}, []);

		layer.setStyle(STYLE);
		layer.setSource(new VectorSource({ features, format: defaultFormat }));

		// Once per model load, if the model boundary extends beyond the current
		// view, fit the map to it so the full analyzed area — and every detection
		// within it — is visible without the user having to zoom out manually.
		const loadKey = `${projectUuid}-${modelUuid}`;
		if (features.length && autoFitKeyRef.current !== loadKey) {
			autoFitKeyRef.current = loadKey;

			const boundaryExtent = layer.getSource().getExtent();
			const size = mapObject?.getSize();
			const viewExtent = size ? mapObject.getView().calculateExtent(size) : null;

			const boundaryOutOfView =
				!userMovedRef.current &&
				boundaryExtent &&
				!isExtentEmpty(boundaryExtent) &&
				viewExtent &&
				!containsExtent(viewExtent, boundaryExtent);

			if (boundaryOutOfView) {
				mapObject.getView().fit(boundaryExtent, {
					padding: [80, 80, 80, 80],
					duration: 600,
					maxZoom: 20,
				});
			}
		}
	}, [layer, data, error]);

	if (!layer || !data) return null;
	return (
		<li>
			<LayerSettings
				id={layerId}
				layer={layer}
				label={layer?.get('name')}
				defaultState={layer?.getVisible()}
				onCheck={() => layer?.setVisible(true)}
				onUncheck={() => layer?.setVisible(false)}
			/>
		</li>
	);
};

export default AoiLayer;
